/**
 * JobBoard - Kanban board for job workflow management
 *
 * Shows workshop statuses from the workflow configuration (showInJobboard: true).
 * Jobs use the job workflow status values.
 */

import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Clock,
  Car,
  User,
  Wrench,
  ChevronRight,
  LogIn,
  Search,
  FileSearch,
  FileClock,
  PackageSearch,
  ClipboardCheck,
  CheckCircle2,
  Archive,
  XCircle,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { boardStyles } from '../../lib/styles';
import { type JobStatus, type WorkflowStatus } from '@leal-crm/shared';

/**
 * Map icon names from shared package to actual Lucide components
 * Each status has a UNIQUE icon for visual distinction
 */
const ICON_MAP: Record<string, LucideIcon> = {
  'clock': Clock,
  'log-in': LogIn,
  'search': Search,
  'file-search': FileSearch,
  'file-clock': FileClock,
  'file-edit': Wrench, // Legacy fallback
  'mail-question': Clock, // For PENDING_CONFIRMATION
  'calendar-check': CheckCircle, // For CONFIRMED
  'wrench': Wrench,
  'package-search': PackageSearch,
  'clipboard-check': ClipboardCheck,
  'check-circle': CheckCircle,
  'car': Car,
  'check-circle-2': CheckCircle2,
  'archive': Archive,
  'x-circle': XCircle,
  'user-x': XCircle, // For NO_SHOW
};

/**
 * Get icon component for a workflow status icon
 */
function getStatusIcon(iconName?: string | null): LucideIcon {
  if (!iconName) return Wrench;
  return ICON_MAP[iconName] || Wrench;
}

/**
 * Get status configuration from workflow statuses or fall back to shared constants
 */
function getStatusConfig(status: string, workflowStatuses?: WorkflowStatus[]) {
  // Try to get from workflow configuration first
  const workflowStatus = workflowStatuses?.find(s => s.code === status);
  const icon = getStatusIcon(workflowStatus?.icon);

  if (workflowStatus) {
    // Use workflow-configured colors (inline styles will be used for these)
    return {
      label: workflowStatus.name,
      color: '', // Will use inline style
      bgColor: '', // Will use inline style
      icon,
      // Raw colors for inline styling
      textColor: workflowStatus.color,
      backgroundColor: workflowStatus.bgColor,
      borderColor: workflowStatus.borderColor,
    };
  }

  return {
    label: status,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50 border-gray-200',
    icon,
  };
}

/**
 * Default column order for the Kanban board
 * Shows workshop statuses (excludes pre-arrival and terminal states)
 * Uses unified booking status values
 */
const DEFAULT_COLUMNS: string[] = [];

/**
 * Format relative date for grouping
 */
function getDateGroup(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Hoy';
  }
  if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Ayer';
  }

  // Check if within last 7 days
  const diffDays = Math.floor((todayOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return date.toLocaleDateString('es-ES', { weekday: 'long' });
  }

  // Otherwise show full date
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

/**
 * Job board item - represents a booking with job in the board
 * Status is from the unified booking status field
 */
export interface JobBoardItem {
  id: string;
  bookingId: string;
  /** When available, deep-link to the unified case container */
  caseId?: string | null;
  bookingReference?: string | null;
  bookingType?: 'private' | 'insurance' | 'warranty';
  status: JobStatus;
  statusInfo?: {
    id: string;
    code: string;
    name: string;
    statusType: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    showInJobboard: boolean;
  } | null;
  estimateAmount: number | null;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | null;
  createdAt: number;
  scheduledAt?: number;
  statusChangedAt?: string | number | null;
  /** Valid transitions from workflow - purely configuration-driven */
  validTransitions?: string[];
  customer: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
  } | null;
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
  } | null;
  service: {
    id: string;
    name: string;
    durationMinutes: number;
    bayType?: 'mechanical' | 'bodywork' | 'diagnostics';
    workflowConfig?: {
      skipStatuses?: string[];
      requiredStatuses?: { status: string; bookingTypes?: string[] }[];
      autoCreateEstimate?: boolean;
    } | null;
  } | null;
  bay: {
    id: string;
    name: string;
    bayType: string;
  } | null;
  assignedStaff: {
    id: string;
    name: string;
  } | null;
}

interface JobCardProps {
  job: JobBoardItem;
  workflowStatuses?: WorkflowStatus[];
  onClick: (job: JobBoardItem) => void;
  onQuickTransition?: (job: JobBoardItem, newStatus: string) => void;
}

// Booking type badge config
const BOOKING_TYPE_BADGES: Record<string, { label: string; className: string }> = {
  private: { label: 'P', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  insurance: { label: 'S', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  warranty: { label: 'G', className: 'bg-purple-100 text-purple-700 border-purple-200' },
};

function JobCard({ job, workflowStatuses, onClick, onQuickTransition }: JobCardProps) {
  // Use workflow-provided validTransitions - no fallback to static rules
  // Show all configured transitions (up to 3)
  const quickTransitions = (job.validTransitions || []).slice(0, 3);
  const typeBadge = job.bookingType ? BOOKING_TYPE_BADGES[job.bookingType] : null;

  return (
    <div className={boardStyles.card}>
      {/* Header row: booking type + reference */}
      <div className="flex items-center gap-1.5 mb-1.5">
        {typeBadge && (
          <span className={cn(
            'text-[9px] font-bold px-1 py-0.5 rounded border',
            typeBadge.className
          )} title={job.bookingType === 'private' ? 'Particular' : job.bookingType === 'insurance' ? 'Seguro' : 'Garantía'}>
            {typeBadge.label}
          </span>
        )}
        {job.bookingReference && (
          <Link
            to={`/bookings/${job.bookingId}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            <span>{job.bookingReference}</span>
            <ExternalLink className="w-2.5 h-2.5" />
          </Link>
        )}
      </div>

      {job.approvalStatus && (
        <div className="mb-1">
          <span className={cn(
            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
            job.approvalStatus === 'approved'
              ? 'bg-green-100 text-green-700'
              : job.approvalStatus === 'rejected'
              ? 'bg-red-100 text-red-700'
              : 'bg-yellow-100 text-yellow-700'
          )}>
            {job.approvalStatus === 'approved' ? 'Aprobado' : job.approvalStatus === 'rejected' ? 'Rechazado' : 'Pendiente aprobación'}
          </span>
        </div>
      )}

      <button onClick={() => onClick(job)} className="w-full text-left">
        {/* Vehicle + Bay */}
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono font-semibold text-xs text-gray-900">
            {job.vehicle?.registration || '—'}
          </span>
          {job.bay && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              {job.bay.name}
            </span>
          )}
        </div>

        {/* Vehicle */}
        <p className={boardStyles.cardSubtitle}>
          {job.vehicle?.make} {job.vehicle?.model}
        </p>

        {/* Customer */}
        <div className={boardStyles.cardMeta}>
          <User className="w-3 h-3" />
          <span className="truncate">{job.customer?.name || '—'}</span>
        </div>

        {/* Service */}
        {job.service && (
          <div className={cn(boardStyles.cardMeta, 'mt-1')}>
            <Wrench className="w-3 h-3" />
            <span className="truncate">{job.service.name}</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-gray-100 text-[10px]">
          {job.estimateAmount !== null ? (
            <span className="font-medium text-green-700">
              {(job.estimateAmount / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
          {job.assignedStaff && <span className="text-gray-500">{job.assignedStaff.name}</span>}
        </div>

        {/* Time in status warning */}
        {(() => {
          if (!job.statusChangedAt) return null;
          const enteredAt = new Date(typeof job.statusChangedAt === 'number' ? job.statusChangedAt * 1000 : job.statusChangedAt).getTime();
          if (isNaN(enteredAt)) return null;
          const minutesInStatus = Math.floor((Date.now() - enteredAt) / (1000 * 60));
          const hours = Math.floor(minutesInStatus / 60);
          const mins = minutesInStatus % 60;
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          // Show warning if more than 2 hours in status
          const isOverdue = minutesInStatus > 120;
          return (
            <div className={cn(
              'flex items-center gap-1 mt-1 text-[10px]',
              isOverdue ? 'text-amber-600 font-medium' : 'text-gray-400'
            )}>
              <Clock className="w-3 h-3" />
              <span>{timeStr} en estado</span>
              {isOverdue && <AlertCircle className="w-3 h-3" />}
            </div>
          );
        })()}
      </button>

      {/* Quick actions */}
      {quickTransitions.length > 0 && onQuickTransition && (
        <div className="pt-1.5 mt-1.5 border-t border-gray-100 flex gap-1 flex-wrap">
          {quickTransitions.map((status) => {
            const config = getStatusConfig(status, workflowStatuses);
            return (
              <button
                key={status}
                onClick={(e) => { e.stopPropagation(); onQuickTransition(job, status); }}
                className={cn(
                  'flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border hover:opacity-80',
                  !config?.textColor && (config?.color || 'text-gray-600'),
                  !config?.backgroundColor && (config?.bgColor || 'bg-gray-50 border-gray-200')
                )}
                style={
                  config?.textColor
                    ? {
                        color: config.textColor,
                        backgroundColor: config.backgroundColor,
                        borderColor: config.borderColor,
                      }
                    : undefined
                }
              >
                <ChevronRight className="w-2.5 h-2.5" />
                {config?.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface KanbanColumnProps {
  status: string;
  jobs: JobBoardItem[];
  workflowStatuses?: WorkflowStatus[];
  onJobClick: (job: JobBoardItem) => void;
  onQuickTransition?: (job: JobBoardItem, newStatus: string) => void;
}

/**
 * Group jobs by date (createdAt) and sort most recent first
 */
function groupJobsByDate(jobs: JobBoardItem[]): Array<{ date: string; jobs: JobBoardItem[] }> {
  // Sort by createdAt descending (most recent first)
  const sorted = [...jobs].sort((a, b) => b.createdAt - a.createdAt);

  // Group by date
  const groups: Map<string, JobBoardItem[]> = new Map();
  for (const job of sorted) {
    const dateGroup = getDateGroup(job.createdAt);
    if (!groups.has(dateGroup)) {
      groups.set(dateGroup, []);
    }
    groups.get(dateGroup)!.push(job);
  }

  return Array.from(groups.entries()).map(([date, jobs]) => ({ date, jobs }));
}

function KanbanColumn({ status, jobs, workflowStatuses, onJobClick, onQuickTransition }: KanbanColumnProps) {
  const config = getStatusConfig(status, workflowStatuses);
  const Icon = config.icon;
  const groupedJobs = useMemo(() => groupJobsByDate(jobs), [jobs]);

  // Determine if using workflow colors (inline styles) or Tailwind classes
  const useInlineStyle = 'textColor' in config;

  return (
    <div className={boardStyles.column}>
      {/* Column header */}
      <div
        className={cn(boardStyles.columnHeader, !useInlineStyle && config.bgColor)}
        style={useInlineStyle ? {
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
        } : undefined}
      >
        <div className="flex items-center gap-1.5">
          <Icon
            className={cn('w-3.5 h-3.5', !useInlineStyle && config.color)}
            style={useInlineStyle ? { color: config.textColor } : undefined}
          />
          <span
            className={cn(boardStyles.columnTitle, !useInlineStyle && config.color)}
            style={useInlineStyle ? { color: config.textColor } : undefined}
          >
            {config.label}
          </span>
        </div>
        <span className={boardStyles.columnCount}>{jobs.length}</span>
      </div>

      {/* Column content */}
      <div className={boardStyles.columnContent}>
        {jobs.length === 0 ? (
          <div className="text-center py-3 text-gray-400 text-[11px]">Sin trabajos</div>
        ) : (
          <div className="space-y-2">
            {groupedJobs.map(({ date, jobs: dateJobs }) => (
              <div key={date}>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wide px-1 mb-1.5">
                  {date}
                </div>
                <div className="space-y-1.5">
                  {dateJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      workflowStatuses={workflowStatuses}
                      onClick={onJobClick}
                      onQuickTransition={onQuickTransition}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface JobBoardProps {
  jobs: JobBoardItem[];
  columns?: string[];
  /** Workflow statuses for dynamic colors/labels */
  workflowStatuses?: WorkflowStatus[];
  onJobClick?: (job: JobBoardItem) => void;
  onQuickTransition?: (job: JobBoardItem, newStatus: string) => void;
  loading?: boolean;
}

export function JobBoard({
  jobs,
  columns = DEFAULT_COLUMNS,
  workflowStatuses,
  onJobClick,
  onQuickTransition,
  loading = false,
}: JobBoardProps) {
  const navigate = useNavigate();

  // Group jobs by unified booking status
  const jobsByStatus = useMemo(() => {
    const grouped: Record<string, JobBoardItem[]> = {};
    for (const col of columns) {
      grouped[col] = [];
    }
    for (const job of jobs) {
      if (grouped[job.status]) {
        grouped[job.status].push(job);
      }
    }
    return grouped;
  }, [jobs, columns]);

  // Handle job click
  const handleJobClick = (job: JobBoardItem) => {
    if (onJobClick) {
      onJobClick(job);
    } else {
      navigate(`/jobs/${job.id}`);
    }
  };

  if (loading) {
    return (
      <div className={boardStyles.container}>
        {columns.map((status) => (
          <div key={status} className={boardStyles.column}>
            <div className="bg-gray-100 animate-pulse rounded-lg h-7 mb-2" />
            <div className="bg-gray-50 rounded-lg p-2 min-h-[150px] space-y-1.5">
              {[1, 2].map((i) => <div key={i} className="bg-white rounded h-24 animate-pulse" />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-gray-500 py-10">
        No hay columnas configuradas para este flujo.
      </div>
    );
  }

  return (
    <div className={boardStyles.container}>
      {columns.map((status) => (
        <KanbanColumn
          key={status}
          status={status}
          jobs={jobsByStatus[status] || []}
          workflowStatuses={workflowStatuses}
          onJobClick={handleJobClick}
          onQuickTransition={onQuickTransition}
        />
      ))}
    </div>
  );
}

export { getStatusConfig };
